/**
 * Organization & Team Management - Type Definitions
 *
 * Comprehensive type system for multi-tenant organizations,
 * team members, roles, and granular permissions
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export type MemberRole = 'owner' | 'admin' | 'member' | 'viewer';

export type TeamVisibility = 'all' | 'admins_only';

export type ClientAccessType = 'all' | 'specific';

// ============================================================================
// ORGANIZATION
// ============================================================================

export interface Organization {
  id: string;
  name: string;
  logo_url?: string | null;
  created_at: string;
  updated_at: string;
  settings: OrganizationSettings;
  created_by?: string;
}

export interface OrganizationSettings {
  team_visibility: TeamVisibility;
  email_domain_restriction: string | null; // e.g., '@ecoemit.com'
  allow_external_invites: boolean;
}

// ============================================================================
// PERMISSIONS
// ============================================================================

export interface MemberPermissions {
  can_view_clients: boolean;
  can_create_clients: boolean;
  can_edit_clients: boolean;
  can_delete_clients: boolean;
  can_manage_team: boolean;
  can_manage_settings: boolean;
}

// Default permissions for each role
export const ROLE_PERMISSIONS: Record<MemberRole, MemberPermissions> = {
  owner: {
    can_view_clients: true,
    can_create_clients: true,
    can_edit_clients: true,
    can_delete_clients: true,
    can_manage_team: true,
    can_manage_settings: true,
  },
  admin: {
    can_view_clients: true,
    can_create_clients: true,
    can_edit_clients: true,
    can_delete_clients: true,
    can_manage_team: true,
    can_manage_settings: false,
  },
  member: {
    can_view_clients: true,
    can_create_clients: true,
    can_edit_clients: true,
    can_delete_clients: false,
    can_manage_team: false,
    can_manage_settings: false,
  },
  viewer: {
    can_view_clients: true,
    can_create_clients: false,
    can_edit_clients: false,
    can_delete_clients: false,
    can_manage_team: false,
    can_manage_settings: false,
  },
};

// Role display names
export const ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

// Role descriptions
export const ROLE_DESCRIPTIONS: Record<MemberRole, string> = {
  owner: 'Full control including billing and organization settings',
  admin: 'Manage team members and access all data',
  member: 'Create and edit client profiles and applications',
  viewer: 'Read-only access to view clients',
};

// ============================================================================
// CLIENT ACCESS FILTER
// ============================================================================

export interface ClientAccessFilter {
  type: ClientAccessType;
  client_ids?: string[];
}

// ============================================================================
// ORGANIZATION MEMBER
// ============================================================================

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: MemberRole;
  permissions: MemberPermissions;
  client_access: ClientAccessFilter;
  invited_by?: string;
  joined_at: string;

  // Joined data from database view
  email?: string;
  full_name?: string;
  organization_name?: string;
  invited_by_email?: string;
}

// Helper type for member with user info
export interface OrganizationMemberWithUser extends OrganizationMember {
  email: string;
  full_name?: string;
}

// ============================================================================
// ORGANIZATION INVITATION
// ============================================================================

export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  email: string;
  role: MemberRole;
  permissions: MemberPermissions;
  client_access: ClientAccessFilter;
  token: string;
  expires_at: string;
  accepted_at?: string;
  invited_by: string;
  created_at: string;

  // Joined data from database view
  organization_name?: string;
  invited_by_email?: string;
  is_valid?: boolean;
  age?: string;
}

// ============================================================================
// FORM TYPES (for UI)
// ============================================================================

export interface InviteMemberFormData {
  email: string;
  role: MemberRole;
  permissions: MemberPermissions;
  client_access: ClientAccessFilter;
}

export interface UpdateMemberFormData {
  role: MemberRole;
  permissions: MemberPermissions;
  client_access: ClientAccessFilter;
}

export interface OrganizationSettingsFormData {
  name: string;
  logo_url?: string | null;
  settings: OrganizationSettings;
}

// ============================================================================
// CLIENT ACCESS QUICK FILTERS
// ============================================================================

export interface ClientAccessQuickFilter {
  type: 'region' | 'applicant_type';
  label: string;
  value: string;
  count: number;
}

export interface ClientAccessFilterState {
  // Quick filters
  selectedRegions: string[];
  selectedTypes: string[];

  // Individual client selection
  selectedClientIds: string[];

  // Derived
  totalSelected: number;
}

// ============================================================================
// HELPER TYPES
// ============================================================================

// Type guard to check if permissions differ from role defaults
export function hasCustomPermissions(
  role: MemberRole,
  permissions: MemberPermissions
): boolean {
  const defaults = ROLE_PERMISSIONS[role];
  return (
    permissions.can_view_clients !== defaults.can_view_clients ||
    permissions.can_create_clients !== defaults.can_create_clients ||
    permissions.can_edit_clients !== defaults.can_edit_clients ||
    permissions.can_delete_clients !== defaults.can_delete_clients ||
    permissions.can_manage_team !== defaults.can_manage_team ||
    permissions.can_manage_settings !== defaults.can_manage_settings
  );
}

// Check if user can perform an action
export function canPerformAction(
  member: OrganizationMember,
  action: keyof MemberPermissions
): boolean {
  return member.permissions[action];
}

// Get permission label
export const PERMISSION_LABELS: Record<keyof MemberPermissions, string> = {
  can_view_clients: 'View clients',
  can_create_clients: 'Create clients',
  can_edit_clients: 'Edit clients',
  can_delete_clients: 'Delete clients',
  can_manage_team: 'Manage team members',
  can_manage_settings: 'Manage organization settings',
};

// Get permission descriptions
export const PERMISSION_DESCRIPTIONS: Record<keyof MemberPermissions, string> = {
  can_view_clients: 'Can view client profiles and their details',
  can_create_clients: 'Can create new client profiles',
  can_edit_clients: 'Can edit existing client profiles',
  can_delete_clients: 'Can delete client profiles',
  can_manage_team: 'Can invite, remove, and manage team members',
  can_manage_settings: 'Can modify organization settings',
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

export function validateEmail(email: string): boolean {
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(email);
}

export function validateEmailDomain(
  email: string,
  domainRestriction: string | null
): boolean {
  if (!domainRestriction) return true;

  const emailDomain = email.split('@')[1];
  const restrictedDomain = domainRestriction.replace('@', '');

  return emailDomain === restrictedDomain;
}

export function isInvitationValid(invitation: OrganizationInvitation): boolean {
  return (
    !invitation.accepted_at &&
    new Date(invitation.expires_at) > new Date()
  );
}

// Check if organization has minimum required admins
export function hasMinimumAdmins(
  members: OrganizationMember[],
  minAdmins: number = 1
): boolean {
  const adminCount = members.filter(m => m.role === 'admin').length;
  return adminCount >= minAdmins;
}

// Check if user can demote themselves
export function canDemoteSelf(
  userId: string,
  members: OrganizationMember[]
): boolean {
  const adminCount = members.filter(m => m.role === 'admin').length;
  const isSelfAdmin = members.find(m => m.user_id === userId && m.role === 'admin');

  // Can't demote if you're the only admin
  return adminCount > 1 && !!isSelfAdmin;
}

// ============================================================================
// DATABASE TYPES (for Supabase queries)
// ============================================================================

export interface Database {
  public: {
    Tables: {
      organizations: {
        Row: Organization;
        Insert: Omit<Organization, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<Organization, 'id'>>;
      };
      organization_members: {
        Row: OrganizationMember;
        Insert: Omit<OrganizationMember, 'id' | 'joined_at'>;
        Update: Partial<Omit<OrganizationMember, 'id' | 'organization_id' | 'user_id'>>;
      };
      organization_invitations: {
        Row: OrganizationInvitation;
        Insert: Omit<OrganizationInvitation, 'id' | 'token' | 'created_at' | 'expires_at'>;
        Update: Partial<Omit<OrganizationInvitation, 'id'>>;
      };
    };
    Views: {
      organization_members_with_users: {
        Row: OrganizationMemberWithUser;
      };
      pending_invitations: {
        Row: OrganizationInvitation & {
          is_valid: boolean;
          age: string;
        };
      };
    };
  };
}
